import dates from 'src/utils/dates'
import durations from 'src/internal/store/utils/durations'

const format = {
  boolean(value) {
    return value ? 'YES' : 'NO'
  },
  hhmm(value) {
    return durations.durationFormatter(value)
  },
  date(dateString, dateFormat = 'MM/DD/YYYY') {
    return dates.getFormattedDate(dateString, dateFormat)
  },
  dateObject(dateObject, dateFormat = 'MM/DD/YYYY') {
    const formatDateObject = () => {
      const { date: { day, month, year } } = dateObject
      return dates.getFormattedDate(`${year}-${month}-${day}`, dateFormat)
    }
    return (dateObject && formatDateObject()) || dates.getFormattedDate('2200-01-01T00:00:00', dateFormat)
  },
  microCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      currency: value.currencyCode,
      currencySign: 'standard',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      signDisplay: 'auto',
      style: 'currency',
    }).format(value.microAmount / 1000000)
  },
  currency(value) {
    return new Intl.NumberFormat('en-US', {
      currency: 'USD',
      currencySign: 'standard',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      signDisplay: 'auto',
      style: 'currency',
    }).format(value)
  },
  dollars(value) {
    return new Intl.NumberFormat('en-US', {
      currency: 'USD',
      currencySign: 'standard',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      signDisplay: 'auto',
      style: 'currency',
    }).format(value)
  },
  number(value) {
    return ((value === 'N/A' || Number.isNaN(value)) && 'N/A') || Number(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  },
  percentage(value) {
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2, style: 'percent' })
  },
  discrepancyPercentage(discrepancyPercentage) {
    const maxInt = '2147483647'

    if ([maxInt, 'N/A'].includes(discrepancyPercentage)) {
      return 'N/A'
    }

    return this.percentage(discrepancyPercentage)
  },

  capitalize(value) {
    const isString = typeof value === 'string'
    const strValue = isString ? value : String(value)
    const capitalize = (word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`

    return (strValue.length && capitalize(value)) || strValue
  },
  titlecase(value) {
    const isString = typeof value === 'string'
    const strValue = isString ? value : String(value)
    const words = strValue.replace(/(?=[A-Z])/g, ' ').split(/(\s|\/){1}/)
    const buildPhrase = (strResult, word) => `${strResult}${this.capitalize(word)}`

    return words.reduce(buildPhrase, '')
  },

  join(value, delimiter) {
    const isArray = Array.isArray(value)
    const notArray = () => !isArray && value
    const joinArray = () => value.length && value.join(delimiter)
    return notArray() || joinArray() || 'None'
  },

  formatValue(value, column) {
    const formatValueAs = column.exportAs || column.formatAs
    const formatArray = () => {
      const [formatterFn, ...args] = formatValueAs
      return this[formatterFn](value, ...args)
    }
    const processArray = () => Array.isArray(formatValueAs) && formatArray()
    const processFormatter = () => typeof this[formatValueAs] === 'function' && this[column.formatAs](value)

    return processArray() || processFormatter() || value
  },
  formatColumn(column, item) {
    return this.formatValue(column.columnValue(item), column)
  },
  getValueFormattedForExport(column, item, exportAs) {
    const ifExportAsIsNull = () => exportAs === null && item[column.key]
    const ifExportAsIsFunctionReference = () => this[exportAs](item)
    const ifExportAsIsFunction = () => exportAs(column.columnValue(item))

    if (typeof exportAs === 'string' && typeof this[exportAs] === 'function') return ifExportAsIsFunctionReference()
    if (typeof exportAs === 'function') return ifExportAsIsFunction()
    return ifExportAsIsNull()
  },
  formatColumnExport(column, normalizedItem) {
    const { exportAs, formatAs, columnValue } = column
    const formatter = exportAs || formatAs

    const getValueWithoutFormatter = () => !formatter && columnValue(normalizedItem)
    const getValueFormattedForExport = () => this.getValueFormattedForExport(column, normalizedItem, exportAs)
    const getValueFormatted = () => format.formatColumn(column, normalizedItem)

    return getValueWithoutFormatter() || getValueFormattedForExport() || getValueFormatted()
  },
}

export default format
